<template>
  <div class="p-4 sm:p-6">

    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-200">{{ t('securityPage.title') }}</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">{{ t('securityPage.subtitle') }}</p>
      </div>
      <button
        @click="openAddModal"
        class="btn mt-3 sm:mt-0 bg-indigo-600 hover:bg-indigo-700 text-white"
      >
         <Icon icon="lucide:plus" class="w-5 h-5 mr-2" />
        {{ t('securityPage.addNewOriginButton') }}
      </button>
    </div>


    <div class="bg-white dark:bg-gray-800 shadow-md rounded-lg border dark:border-gray-700 overflow-hidden">
      <div class="p-4 sm:p-6">
         <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
           <strong class="font-medium text-orange-500 dark:text-orange-400">{{ t('securityPage.noteTitle') }}</strong> {{ t('securityPage.noteText') }}
         </p>
      
        <div class="overflow-x-auto">
           <ThemeOnly theme="modern" :invert="true">
             <EasyDataTable
               :headers="[
                 { text: t('securityPage.headers.originUrl'), value: 'origin', sortable: true },
               { text: t('securityPage.headers.addedOn'), value: 'createdAt', sortable: true },
               { text: t('securityPage.headers.options'), value: 'options', width: 140, align: 'center' }
             ]"
             :items="allowedOrigins"
             :loading="fetchLoading"
             theme-color="#4f46e5"
             table-class-name="w-full custom-security-table"
             header-text-direction="left"
             body-text-direction="left"
             buttons-pagination
             :empty-message="t('securityPage.emptyMessage')"
           >
              <template #loading>
                   <ModernLoader />
              </template>

              <template #item-createdAt="{ createdAt }">
                 {{ formatDate(createdAt) }}
              </template>

              <template #item-options="item">
                   <button
                     @click="openDeleteConfirmationModal(item)"
                     :disabled="isLoading === true"
                     class="hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                   >
                     {{ t('securityPage.deleteModal.deleteButton') }}
                   </button>
               </template>
           </EasyDataTable>
           </ThemeOnly>
         </div>
      </div>
    </div>


     <it-modal v-model="isAddModalOpen">
       <div class="p-1s max-w-xl w-full">
         <div class="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
           <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{{ t('securityPage.addModal.title') }}</h3>
           <div class="space-y-4">
             <p class="text-sm text-gray-600 dark:text-gray-400">{{ t('securityPage.addModal.description') }}</p>
             <div class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
               <label for="newOriginsInputModal" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{ t('securityPage.addModal.originUrlsLabel') }}</label>
               <textarea
                 id="newOriginsInputModal"
                 v-model="newOriginsInput"
                 :placeholder="t('securityPage.addModal.placeholder')"
                 rows="5"
                 class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-gray-200 font-mono text-sm"
                 :disabled="isLoading === true"
               ></textarea>
               <p v-if="isDefinedAndNotEmpty(addError) != false" class="text-red-500 text-xs mt-1">{{ addError }}</p>
             </div>
             <div v-if="addResults.length > 0" class="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto border dark:border-gray-600 p-3 rounded-md bg-gray-100 dark:bg-gray-900">
                <h4 class="font-medium text-gray-800 dark:text-gray-300 mb-2">{{ t('securityPage.addModal.resultsTitle') }}</h4>
                <p v-for="(result, index) in addResults" :key="index" :class="result.success === true ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                  <strong class="font-mono break-all">{{ result.origin }}</strong>: {{ result.message }}
                </p>
              </div>
           </div>
         </div>
         <div class="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200 dark:border-gray-700">
           <button
             @click="addMultipleOrigins"
             :disabled="newOriginsInput.trim() === '' || isLoading === true"
             class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
           >
             <Icon v-if="isLoading === true" icon="line-md:loading-twotone-loop" class="w-5 h-5 mr-2" />
             {{ t('securityPage.addModal.addOriginsButton') }}
           </button>
           <button @click="closeAddModal" class="mr-3 inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500">
             Cancel
           </button>
         </div>
       </div>
     </it-modal>

     <it-modal v-model="isDeleteModalOpen">
       <div class="p-4">
         <h2 class="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{{ t('securityPage.deleteModal.title') }}</h2>
         <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
           {{ t('securityPage.deleteModal.description').replace('{origin}', originToDelete?.origin != null ? originToDelete.origin : '') }}
         </p>
         <div class="flex justify-end space-x-3">
           <button @click="isDeleteModalOpen = false" class="btn btn-ghost">
             Cancel
           </button>
           <button
             @click="confirmDeleteOrigin"
             :disabled="isLoading === true"
             class="btn bg-red-600 hover:bg-red-700 text-white"
           >
             <Icon v-if="isLoading === true" icon="line-md:loading-twotone-loop" class="w-5 h-5 mr-2" />
             {{ t('securityPage.deleteModal.deleteButton') }}  
           </button>
         </div>
       </div>
     </it-modal>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { Icon } from "@iconify/vue/dist/offline";
import axios from 'axios';
import EasyDataTable from 'vue3-easy-data-table';
import 'vue3-easy-data-table/dist/style.css';
import { globalState } from '@/globalState';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { useI18n } from '@/i18n';
import ModernLoader from '@/components/ModernLoader.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
const { t } = useI18n();

const apiEndpoint = globalState.apiEndpoint;
const allowedOrigins = ref([]);
const newOriginsInput = ref('');
const isLoading = ref(false);
const fetchLoading = ref(false);
const addError = ref('');
const fetchError = ref('');
const addResults = ref([]);

const isAddModalOpen = ref(false);
const isDeleteModalOpen = ref(false);
const originToDelete = ref(null);

const headers = [
  { text: "Origin URL", value: "origin", sortable: true },
  { text: "Added On", value: "createdAt", sortable: true },
  { text: "Options", value: "options", width: 80, align: 'center' },
];

const formatDate = (dateString) => {
  if (isDefinedAndNotEmpty(dateString) == false) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime()) === true) {
        console.warn('Invalid date string received:', dateString);
        return 'Invalid Date';
    }
    return date.toLocaleString();
  } catch (e) {
    console.error('Error formatting date:', e, 'Input:', dateString);
    return dateString;
  }
};

const fetchOrigins = async () => {
  fetchLoading.value = true;
  fetchError.value = '';
  allowedOrigins.value = []; 
  try {
    const response = await axios.get(`${apiEndpoint}/api/organizationSettings/allowedOrigins`, { withCredentials: true });
    allowedOrigins.value = response.data;
  } catch (error) {
    console.error('Error fetching allowed origins:', error);
    fetchError.value = isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : isDefinedAndNotEmpty(error.message) != false ? error.message : 'Could not fetch origins.';
  } finally {
    fetchLoading.value = false;
  }
};

const openAddModal = () => {
  newOriginsInput.value = '';
  addError.value = '';
  addResults.value = [];
  isAddModalOpen.value = true;
};

const closeAddModal = () => {
  isAddModalOpen.value = false;
};

const addMultipleOrigins = async () => {
  if (newOriginsInput.value.trim() === '' || isLoading.value !== false) return;

  isLoading.value = true;
  addError.value = '';
  addResults.value = [];

  const originsToAdd = newOriginsInput.value
    .split(/\r?\n/)
    .map(origin => origin.trim())
    .filter(origin => isDefinedAndNotEmpty(origin) != false && origin !== '');

  if (originsToAdd.length === 0) {
    addError.value = 'Please enter at least one valid origin URL.';
    isLoading.value = false;
    return;
  }

  const results = [];
  let requiresRefresh = false; 

  for (const origin of originsToAdd) {
    let currentResult = { origin: origin, success: false, message: '' };
    try {
      try {
        const url = new URL(origin);
        if (url.pathname !== '/' || url.search !== '' || url.hash !== '') {
          throw new Error('Invalid format (origin only).');
        }
      } catch (e) {
        throw new Error(isDefinedAndNotEmpty(e.message) != false ? e.message : 'Invalid URL format.');
      }

      const response = await axios.post(`${apiEndpoint}/api/organizationSettings/allowedOrigins`,
        { origin: origin },
        { withCredentials: true }
      );
      currentResult.success = true;
      currentResult.message = 'Added successfully.';
      requiresRefresh = true; 

    } catch (error) {
      console.error(`Error adding origin ${origin}:`, error);
      if (error.response?.status === 409) {
        currentResult.message = 'Already exists.';
      } else {
        currentResult.message = isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : isDefinedAndNotEmpty(error.message) != false ? error.message : 'Failed to add.';
      }
    } finally {
      results.push(currentResult);
    }
  }

  addResults.value = results;
  if (requiresRefresh === true) {
      newOriginsInput.value = ''; 
      await fetchOrigins(); 
      closeAddModal();
  }
  isLoading.value = false;
};

const openDeleteConfirmationModal = (item) => {
  if (isLoading.value === true) return;
  originToDelete.value = item;
  isDeleteModalOpen.value = true;
};

const confirmDeleteOrigin = async () => {
  if (originToDelete.value === null || isLoading.value !== false) return;

  isLoading.value = true;
  const originId = originToDelete.value.originId;

  if (originId === undefined || originId === null) {
      console.error("Cannot delete origin: originId is missing from the item.", originToDelete.value);
      alert("Cannot delete origin: ID is missing.");
      isLoading.value = false;
      return;
  }

  try {
    await axios.delete(`${apiEndpoint}/api/organizationSettings/allowedOrigins/${originId}`, { withCredentials: true });
    allowedOrigins.value = allowedOrigins.value.filter(o => o.originId !== originId);
    isDeleteModalOpen.value = false;
    originToDelete.value = null;
  } catch (error) {
    console.error('Error deleting origin:', error);
    alert(`Failed to delete origin: ${isDefinedAndNotEmpty(error.response?.data?.error) != false ? error.response?.data?.error : isDefinedAndNotEmpty(error.message) != false ? error.message : 'Unknown error'}`); 
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchOrigins);
</script>

<style>
.custom-security-table {
  --easy-table-header-font-size: 14px;
  --easy-table-body-row-font-size: 14px;
  --easy-table-border: 1px solid #e5e7eb; 
  --easy-table-row-border: 1px solid #e5e7eb;
}
.dark .custom-security-table {
  --easy-table-border: 1px solid #374151; 
  --easy-table-row-border: 1px solid #374151;
  --easy-table-header-background-color: #1f2937; 
  --easy-table-header-font-color: #d1d5db; 
  --easy-table-body-row-background-color: #374151; 
  --easy-table-body-row-font-color: #f3f4f6; 
   --easy-table-footer-background-color: #1f2937; 
   --easy-table-footer-font-color: #d1d5db; 
}
</style> 